import {RoomImage} from "../../../api/apiClient";
import {alpha, Box, Chip, ListItemButton, Skeleton, Stack, Typography} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useState} from "react";

const ROW_HEIGHT_PX = 64;
const THUMBNAIL_WIDTH_PX = 64;
const THUMBNAIL_HEIGHT_PX = 44;

// The "you are here" chip. Smaller than MUI's small chip, which is sized to sit on its own rather than
// underneath a room name as a second line.
const CURRENT_CHIP_HEIGHT_PX = 18;
const CURRENT_CHIP_FONT_SIZE_PX = 11;

/** Enough tint to read as the room's own marker, but not enough to compete with the room name. */
const CURRENT_CHIP_BACKGROUND_OPACITY = 0.12;

export interface RoomRowProps {
    room: RoomImage;
    isCurrent: boolean;
    /** False when every room is on the same floor. */
    showFloor: boolean;
    onClick: (panorama: string) => void;
}

/** A single room in the room list: thumbnail, name, and either its floor or a "you are here" marker. */
export function RoomRow(props: RoomRowProps) {
    const {room, isCurrent, showFloor, onClick} = props;

    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isImageError, setIsImageError] = useState(false);

    // A room whose image is missing is dropped entirely, as before.
    if (isImageError) {
        return <></>;
    }

    return <ListItemButton
        divider
        onClick={() => onClick(room.targetPanorama)}
        sx={{
            minHeight: ROW_HEIGHT_PX,
            gap: 1.5,
            px: 0,
            py: 1
        }}>

        <Box sx={{
            position: "relative",
            flexShrink: 0,
            width: THUMBNAIL_WIDTH_PX,
            height: THUMBNAIL_HEIGHT_PX,
            borderRadius: 1,
            overflow: "hidden"
        }}>
            {!isImageLoaded && <Skeleton variant={"rectangular"} width={"100%"} height={"100%"}/>}

            <img src={room.image}
                 alt={room.imageHeader}
                 loading={"lazy"}
                 onLoad={() => setIsImageLoaded(true)}
                 onError={() => setIsImageError(true)}
                 style={{
                     width: "100%",
                     height: "100%",
                     objectFit: "cover",
                     display: "block",
                     // Kept in the DOM so lazy loading still triggers, but hidden behind the skeleton
                     // until it has something to show.
                     position: isImageLoaded ? "static" : "absolute",
                     visibility: isImageLoaded ? "visible" : "hidden"
                 }}/>
        </Box>

        <Stack sx={{flexGrow: 1, minWidth: 0}}>
            <Typography variant={"body1"} fontWeight={600} noWrap>{room.imageHeader}</Typography>

            {isCurrent
                ? <Chip label={"You are here"}
                        size={"small"}
                        sx={{
                            alignSelf: "flex-start",
                            height: CURRENT_CHIP_HEIGHT_PX,
                            backgroundColor: theme =>
                                alpha(theme.palette.primary.main, CURRENT_CHIP_BACKGROUND_OPACITY),
                            color: "primary.main",
                            "& .MuiChip-label": {
                                px: 0.75,
                                fontSize: CURRENT_CHIP_FONT_SIZE_PX,
                                fontWeight: 600
                            }
                        }}/>
                : showFloor && room.floor &&
                    <Typography variant={"caption"} color={"text.secondary"} noWrap>{room.floor}</Typography>}
        </Stack>

        <ChevronRightIcon fontSize={"small"} sx={{flexShrink: 0, color: "text.secondary"}}/>
    </ListItemButton>
}
